import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import React, { useContext } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import FarmSelectionCard from './FarmSelectionCard'
import { useLocation, useNavigate } from 'react-router-dom'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { useDispatch } from 'react-redux'
import { setActiveStorage } from 'src/State/Storage/StorageActions'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { G1Nowifi } from '@e3dc-react/icons'
import { getClientsForFarmSelector } from '../Framework/utils/framework'
import { useFarmContext } from './provider'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  dialogPaper: {
    [theme.breakpoints.down('md')]: {
      width: 'auto !important',
    },
  },
}))

export interface FarmParticipantSelectorProps {
  place: number
  onClose: () => void
  selectedOptionStorageId: string
}

/**
 * Dialog for selecting a farm participant.
 *
 * Has responsive full-screen behavior based on the number of farm participant options and screen size.
 * Behaves on mobiles screens different than in other places in case there is only 1 or 2 farm participant options,
 * then the dialog will automatically use full-screen mode. In other cases on mobile screens, it will not use full-screen mode.
 * This behavior ensures an optimal user experience on both mobile and larger screens.
 */
const FarmParticipantSelector: React.FC<FarmParticipantSelectorProps> = ({ place, onClose, selectedOptionStorageId }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { isServerOnline, offlineClientStorageIds } = useContext(InstallationOnlineStatusContext)
  const { farmMaster, farmClients } = useFarmContext()
  const { classes } = useStyles()

  const farmParticipantOptions = getClientsForFarmSelector(farmMaster, farmClients)

  const xsColumns = Math.max(1, Math.min(farmParticipantOptions.length, 2))
  const smColumns = Math.max(1, Math.min(farmParticipantOptions.length, 3))
  const mdUpColumns = Math.max(1, Math.min(farmParticipantOptions.length, 4))
  const isSmDown = useMediaQuery(`${theme.breakpoints.down('sm')}`)
  const isMdDown = useMediaQuery(`${theme.breakpoints.down('md')}`)
  const useFullScreen = (isSmDown && farmParticipantOptions.length > 1) || (isMdDown && farmParticipantOptions.length > 2)

  const handleClose = (): void => {
    onClose()
  }

  const handleCardClick = (storageId: string): void => {
    if (selectedOptionStorageId === storageId) return
    const newPathName = location.pathname.replace(`${selectedOptionStorageId}`, `${storageId}`)
    navigate(newPathName)
    dispatch(setActiveStorage(storageId.toString()))
    handleClose()
  }

  const actions: DialogActions = {
    secondary: [
      {
        name: t('common.cancel'),
      },
    ],
  }

  return (
    <Dialog
      place={place}
      onClose={() => handleClose()}
      title={t('farm.farm-participant-selector.title')}
      actions={actions}
      fullWidth={false}
      showCloseButton
      maxWidth={false}
      useFullScreen={useFullScreen}
      componentClasses={useFullScreen ? undefined : { paper: classes.dialogPaper }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          pt: 1,
          gridTemplateColumns: {
            xs: `repeat(${xsColumns}, minmax(0, 1fr))`,
            sm: `repeat(${smColumns}, minmax(0, 1fr))`,
            md: `repeat(${mdUpColumns}, minmax(0, 1fr))`,
          },
        }}
      >
        {farmParticipantOptions.map((participant, index) => {
          const isSelected = participant.storageId === selectedOptionStorageId
          const isOffline =
            (!participant.isClient && !isServerOnline) || (participant.isClient && offlineClientStorageIds?.includes(participant.storageId || ''))

          return (
            <Box key={`farm-participant-${participant.storageId}-${index}`} sx={{ position: 'relative' }}>
              {isOffline && (
                <G1Nowifi
                  sx={{
                    position: 'absolute',
                    top: 3,
                    right: 2,
                    zIndex: theme.zIndex.appBar - 1,
                    color: theme.shape.snackbar.backgroundColor.errorLight,
                    fontSize: 30,
                    backgroundColor: 'error.main',
                    borderRadius: 4,
                  }}
                />
              )}
              <FarmSelectionCard
                onClick={() => handleCardClick(participant.storageId)}
                storageId={participant.storageId}
                isClient={participant.isClient}
                variant={farmParticipantOptions.length <= 4 ? 'large' : 'small'}
                productType={participant.productType}
                isSelected={isSelected}
              />
            </Box>
          )
        })}
      </Box>
    </Dialog>
  )
}

export default FarmParticipantSelector
